import axios from "axios";

// Files in flight at once. Enough to overlap each file's request overhead
// without saturating a clinic's uplink.
export const UPLOAD_CONCURRENCY = 4;
// POST /mediaFile/complete accepts at most this many ids per call.
const COMPLETE_BATCH_SIZE = 50;
// complete is idempotent, so a failed call is simply asked again.
const COMPLETE_RETRY_DELAYS_MS = [1000, 3000];

// Requests to S3 (presigned URLs) carry their own credentials. They must not
// get the app's Authorization header, and an S3 403 (an expired URL) must not
// be mistaken for a permission problem by the global interceptor, which would
// redirect the user away.
export const S3_REQUEST_CONFIG = {
  skipAuthInterceptor: true,
  skipPermissionRedirect: true,
  withCredentials: false
};

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Runs `worker` over `items`, keeping at most `limit` in flight.
 */
export async function mapWithLimit(items, limit, worker) {
  const queue = [...items];
  const runners = Array.from(
    { length: Math.min(limit, queue.length) },
    async () => {
      while (queue.length > 0) {
        await worker(queue.shift());
      }
    }
  );
  await Promise.all(runners);
}

/**
 * PUTs one file to its presigned URL. Resolves once S3 has stored it.
 *
 * @param {string} url
 * @param {Blob} file
 * @param {string} contentType - must equal the type the URL was signed for
 * @param {(loaded: number) => void} [onProgress] - bytes sent so far
 * @param {import("axios").CancelToken} [cancelToken] - aborts the transfer
 */
export function putToS3(url, file, contentType, onProgress, cancelToken) {
  return axios.put(url, file, {
    ...S3_REQUEST_CONFIG,
    cancelToken,
    headers: { "Content-Type": contentType },
    transformRequest: [
      (data, headers) => {
        delete headers["Authorization"];
        if (headers.common) delete headers.common["Authorization"];
        return data;
      }
    ],
    onUploadProgress: event => {
      if (onProgress) onProgress(event.loaded);
    }
  });
}

const isExpiredUrl = error =>
  !!(error && error.response && error.response.status === 403);

async function completeWithRetry(complete, uploadIds) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await complete(uploadIds);
    } catch (error) {
      if (attempt >= COMPLETE_RETRY_DELAYS_MS.length) throw error;
      await sleep(COMPLETE_RETRY_DELAYS_MS[attempt]);
    }
  }
}

/**
 * Uploads files the way the server expects: presign all, PUT them to S3 a few
 * at a time, then register them with one complete call. The server's answer is
 * the definite result — no polling for the files to appear.
 *
 * @param {Object} options
 * @param {Blob[]} options.files
 * @param {string[]} options.contentTypes - per file; what presign signs and the PUT sends
 * @param {(indexes: number[]) => Promise<Array<{uploadId: number, url: string}>>} options.presign
 *   presigns the files at these indexes, in order. Rejections (quota, validation)
 *   propagate before anything is uploaded.
 * @param {(uploadIds: number[]) => Promise<{completed: Array, failed: Array}>} options.complete
 * @param {(status: {done: number, failed: number, total: number, percent: number, items: Array<{index: number, state: string, loaded: number}>}) => void} [options.onProgress]
 *   `items` gives each file's state: queued | uploading | uploaded | done | failed
 * @param {() => void} [options.onPresigned] - called once every file has a URL,
 *   i.e. the server accepted the batch (quota, validation) and only the
 *   transfer is left. A dialog can close here and leave progress to the toast.
 * @param {{isCancelled: () => boolean, token?: import("axios").CancelToken}} [options.cancel]
 *   stops files that have not finished sending; they fail as "cancelled".
 *   Files already in S3 are still registered.
 * @returns {Promise<{
 *   completed: Array<{index: number, uploadId: number, file: Object}>,
 *   failed: Array<{index: number, reason: string}>
 * }>}
 */
export async function uploadBatch({
  files,
  contentTypes,
  presign,
  complete,
  onProgress,
  onPresigned,
  cancel
}) {
  const isCancelled = () => !!(cancel && cancel.isCancelled());
  const items = files.map((file, index) => ({
    index,
    file,
    contentType: contentTypes[index],
    uploadId: null,
    url: null,
    loaded: 0,
    state: "queued", // queued | uploading | uploaded | done | failed
    reason: null
  }));
  const totalBytes = items.reduce((sum, it) => sum + (it.file.size || 0), 0);

  const report = () => {
    if (!onProgress) return;
    const loaded = items.reduce((sum, it) => sum + it.loaded, 0);
    onProgress({
      done: items.filter(it => it.state === "uploaded" || it.state === "done")
        .length,
      failed: items.filter(it => it.state === "failed").length,
      total: items.length,
      percent: totalBytes ? Math.min(100, Math.round((loaded / totalBytes) * 100)) : 0,
      items: items.map(it => ({ index: it.index, state: it.state, loaded: it.loaded }))
    });
  };
  const fail = (item, reason) => {
    item.state = "failed";
    item.reason = reason;
    report();
  };

  const slots = await presign(items.map(it => it.index));
  items.forEach((item, i) => {
    item.uploadId = slots[i].uploadId;
    item.url = slots[i].url;
  });
  report();
  if (onPresigned) onPresigned();

  const put = item =>
    putToS3(
      item.url,
      item.file,
      item.contentType,
      loaded => {
        item.loaded = loaded;
        report();
      },
      cancel && cancel.token
    );

  await mapWithLimit(items, UPLOAD_CONCURRENCY, async item => {
    if (isCancelled()) return fail(item, "cancelled");
    item.state = "uploading";
    report();
    try {
      await put(item);
    } catch (error) {
      if (axios.isCancel(error) || isCancelled()) {
        item.loaded = 0;
        return fail(item, "cancelled");
      }
      if (!isExpiredUrl(error)) return fail(item, "upload");
      // The URL expired while the file waited its turn: sign it again once.
      try {
        const [slot] = await presign([item.index]);
        item.uploadId = slot.uploadId;
        item.url = slot.url;
        item.loaded = 0;
        await put(item);
      } catch (retryError) {
        const cancelled = axios.isCancel(retryError) || isCancelled();
        return fail(item, cancelled ? "cancelled" : "upload");
      }
    }
    item.loaded = item.file.size || 0;
    item.state = "uploaded";
    report();
  });

  const uploaded = items.filter(it => it.state === "uploaded");
  const byUploadId = new Map(uploaded.map(it => [it.uploadId, it]));
  const completed = [];

  for (let i = 0; i < uploaded.length; i += COMPLETE_BATCH_SIZE) {
    const batch = uploaded.slice(i, i + COMPLETE_BATCH_SIZE);
    try {
      const result = await completeWithRetry(
        complete,
        batch.map(it => it.uploadId)
      );
      (result.completed || []).forEach(({ uploadId, file }) => {
        const item = byUploadId.get(uploadId);
        if (!item) return;
        item.state = "done";
        completed.push({ index: item.index, uploadId, file });
      });
      (result.failed || []).forEach(({ uploadId, reason }) => {
        const item = byUploadId.get(uploadId);
        if (item) fail(item, reason);
      });
    } catch (error) {
      // The files are in S3; the server registers them on its own shortly.
      batch.forEach(item => fail(item, "server"));
    }
  }
  // Anything the server answered neither way.
  items
    .filter(it => it.state === "uploaded")
    .forEach(it => fail(it, "server"));

  report();
  return {
    completed: completed.sort((a, b) => a.index - b.index),
    failed: items
      .filter(it => it.state === "failed")
      .map(it => ({ index: it.index, reason: it.reason }))
  };
}
